import { Routes, Route, Outlet, Navigate, useLocation, useSearchParams } from "react-router-dom";

import { AppLayout, AuthLayout, Loader } from "components";
import { useAuth } from "hooks";
import { getLocationPathSearchHash, isEmptyPath } from "utils";
import { lazyPage } from "utils/lazy-page";

const Dashboard = lazyPage(() => import("features/dashboard/pages/Dashboard"));
const Documentation = lazyPage(() => import("features/documentation/pages/Documentation"));
const Account = lazyPage(() => import("features/account/pages/Account"));
const Login = lazyPage(() => import("features/login/pages/Login"));
const PrepaidPackages = lazyPage(() => import("features/prepaid-packages/pages/PrepaidPackages"));
const RegisterRequest = lazyPage(() => import("features/register-request/pages/RegisterRequest"));
const SubscriptionPay = lazyPage(() => import("features/subscription-pay/pages/SubscriptionPay"));
const ResetPassword = lazyPage(() => import("features/reset-password/pages/ResetPassword"));

export const AppRouter = () => {
  const { isLoading, user } = useAuth();
  const isLoggedIn = !!user;

  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");

  const location = useLocation();
  const currentLocation = getLocationPathSearchHash(location);

  if (isLoading) {
    return <Loader size="lg" spread />;
  }
  if (isLoggedIn && nextPath) {
    return <Navigate to={nextPath} replace />;
  }

  return (
    <>
      {isLoggedIn ? (
        <Routes>
          <Route
            path="dashboard"
            element={
              <AppLayout>
                <Outlet />
              </AppLayout>
            }
          >
            <Route index element={<Dashboard />} />
            {!user?.is_enterprise && <Route path="packages" element={<PrepaidPackages />} />}
            <Route path="docs" element={<Documentation />} />
            <Route path="account" element={<Account />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>
          <Route
            path="password-reset"
            element={
              <AuthLayout>
                <ResetPassword />
              </AuthLayout>
            }
          />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      ) : (
        <AuthLayout>
          <Routes>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<RegisterRequest />} />
            <Route path="subscription-pay" element={<SubscriptionPay />} />
            <Route path="password-reset" element={<ResetPassword />} />
            <Route
              path="*"
              element={
                <Navigate
                  to={{
                    pathname: "login",
                    search: !isEmptyPath(currentLocation)
                      ? new URLSearchParams([["next", currentLocation]]).toString()
                      : undefined,
                  }}
                  replace
                />
              }
            />
          </Routes>
        </AuthLayout>
      )}
    </>
  );
};
